<template>
  <div class="dock-bottleneck-calculator" data-theme="v1">
    <!-- Main Content -->
    <div class="dock-bottleneck-calculator__container">
      <!-- Page Header -->
      <div class="dock-bottleneck-calculator__page-header">
        <div class="dock-bottleneck-calculator__header-content">
          <div class="dock-bottleneck-calculator__header-icon">
            <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 7h6m0 10v-3m-3 3h.01M9 17h.01M9 14h.01M12 14h.01M15 11h.01M12 11h.01M9 11h.01M7 21h10a2 2 0 002-2V5a2 2 0 00-2-2H7a2 2 0 00-2 2v14a2 2 0 002 2z"></path>
            </svg>
          </div>
          <div>
            <h2 class="dock-bottleneck-calculator__page-title">Industrial Dock & Yard Bottleneck Cost Calculator</h2>
            <p class="dock-bottleneck-calculator__page-description">Calculate the financial impact of dock congestion and yard delays</p>
          </div>
        </div>
      </div>

      <!-- Calculator Form -->
      <div class="dock-bottleneck-calculator__form-section">
        <form @submit.prevent="calculate">
          <!-- Section A: The Bottlenecks -->
          <div class="calculator-section">
            <h3 class="calculator-section__title">A. Dock & Yard Inputs</h3>
            <div class="calculator-section__grid">
              <div class="form-group">
                <label for="trucksArriving">Trucks Arriving During Peak Window</label>
                <input 
                  type="number" 
                  id="trucksArriving" 
                  v-model.number="trucksArriving" 
                  min="0"
                  required
                  class="form-input"
                  placeholder="e.g., 18"
                />
                <span class="form-help">Number of trucks arriving during the busiest period</span>
              </div>

              <div class="form-group">
                <label for="usableDoors">Usable Dock Doors</label>
                <input 
                  type="number" 
                  id="usableDoors" 
                  v-model.number="usableDoors" 
                  min="0"
                  required
                  class="form-input"
                  placeholder="e.g., 4"
                />
                <span class="form-help">Total number of available dock doors</span>
              </div>

              <div class="form-group">
                <label for="dockTurnTime">Average Dock Turn Time (hours)</label>
                <input 
                  type="number" 
                  id="dockTurnTime" 
                  v-model.number="dockTurnTime" 
                  step="0.01" 
                  min="0"
                  required
                  class="form-input"
                  placeholder="e.g., 2.5"
                />
                <span class="form-help">Time a truck spends at the dock (live load)</span>
              </div>

              <div class="form-group">
                <label for="carrierFreeTime">Carrier Free Time (hours)</label>
                <input 
                  type="number" 
                  id="carrierFreeTime" 
                  v-model.number="carrierFreeTime" 
                  step="0.01" 
                  min="0"
                  required
                  class="form-input"
                  placeholder="e.g., 2.0"
                />
                <span class="form-help">Grace period before detention fees apply</span>
              </div>
            </div>
          </div>

          <!-- Section B: The Cost Factors -->
          <div class="calculator-section">
            <h3 class="calculator-section__title">B. Cost Inputs</h3>
            <div class="calculator-section__grid">
              <div class="form-group">
                <label for="detentionFee">Detention Fee ($/hour/truck)</label>
                <input 
                  type="number" 
                  id="detentionFee" 
                  v-model.number="detentionFee" 
                  step="0.01" 
                  min="0"
                  required
                  class="form-input"
                  placeholder="e.g., 75"
                />
                <span class="form-help">Fee charged by carrier per hour per truck</span>
              </div>

              <div class="form-group">
                <label for="warehouseLabor">Warehouse Labor Affected (operators)</label>
                <input 
                  type="number" 
                  id="warehouseLabor" 
                  v-model.number="warehouseLabor" 
                  min="0"
                  required
                  class="form-input"
                  placeholder="e.g., 3"
                />
                <span class="form-help">Number of forklift operators affected</span>
              </div>

              <div class="form-group">
                <label for="laborCost">Fully Burdened Labor Cost ($/hour)</label>
                <input 
                  type="number" 
                  id="laborCost" 
                  v-model.number="laborCost" 
                  step="0.01" 
                  min="0"
                  required
                  class="form-input"
                  placeholder="e.g., 30"
                />
                <span class="form-help">Cost per operator per hour</span>
              </div>

              <div class="form-group">
                <label for="overtimeIdleTime">Overtime/Idle Time (hours)</label>
                <input 
                  type="number" 
                  id="overtimeIdleTime" 
                  v-model.number="overtimeIdleTime" 
                  step="0.01" 
                  min="0"
                  required
                  class="form-input"
                  placeholder="e.g., 2"
                />
                <span class="form-help">Hours of overtime or idle time caused by congestion</span>
              </div>

              <div class="form-group">
                <label for="palletsPerTruck">Average Pallets Per Truck</label>
                <input 
                  type="number" 
                  id="palletsPerTruck" 
                  v-model.number="palletsPerTruck" 
                  min="0"
                  required
                  class="form-input"
                  placeholder="e.g., 26"
                />
                <span class="form-help">Average number of pallets per truck</span>
              </div>

              <div class="form-group">
                <label for="inventoryCost">Inventory Carrying Cost ($/pallet/day)</label>
                <input 
                  type="number" 
                  id="inventoryCost" 
                  v-model.number="inventoryCost" 
                  step="0.01" 
                  min="0"
                  required
                  class="form-input"
                  placeholder="e.g., 1.50"
                />
                <span class="form-help">Opportunity cost per pallet per day</span>
              </div>
            </div>
          </div>

          <!-- Calculate Button -->
          <div class="calculator-actions">
            <button type="submit" class="btn-calculate">
              <svg class="btn-icon" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 7h6m0 10v-3m-3 3h.01M9 17h.01M9 14h.01M12 14h.01M15 11h.01M12 11h.01M9 11h.01M7 21h10a2 2 0 002-2V5a2 2 0 00-2-2H7a2 2 0 00-2 2v14a2 2 0 002 2z"></path>
              </svg>
              Calculate Yard Congestion Cost
            </button>
            <button type="button" @click="reset" class="btn-reset">
              Reset
            </button>
          </div>
        </form>
      </div>

      <!-- Results Section -->
      <div v-if="results" class="dock-bottleneck-calculator__results">
        <h3 class="results-title">Total Daily Yard Bottleneck Cost</h3>
        
        <!-- Summary Cards -->
        <div class="results-grid">
          <div class="result-card result-card--primary">
            <div class="result-card__icon">
              <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z"></path>
              </svg>
            </div>
            <div class="result-card__content">
              <div class="result-card__label">Total Daily Cost</div>
              <div class="result-card__value">${{ formatNumber(results.totalCost) }}</div>
            </div>
          </div>

          <div class="result-card">
            <div class="result-card__icon result-card__icon--warning">
              <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"></path>
              </svg>
            </div>
            <div class="result-card__content">
              <div class="result-card__label">Detention Cost</div>
              <div class="result-card__value">${{ formatNumber(results.detentionCost) }}</div>
              <div class="result-card__formula">{{ formatNumber(results.totalDetentionHours) }} hrs × ${{ formatNumber(detentionFee) }}/hr</div>
            </div>
          </div>

          <div class="result-card">
            <div class="result-card__icon result-card__icon--info">
              <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z"></path>
              </svg>
            </div>
            <div class="result-card__content">
              <div class="result-card__label">Labor Cost (Idle/Overtime)</div>
              <div class="result-card__value">${{ formatNumber(results.laborCost) }}</div>
              <div class="result-card__formula">{{ warehouseLabor }} operators × {{ formatNumber(overtimeIdleTime) }} hrs × ${{ formatNumber(laborCost) }}/hr</div>
            </div>
          </div>

          <div class="result-card">
            <div class="result-card__icon result-card__icon--purple">
              <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4"></path>
              </svg>
            </div>
            <div class="result-card__content">
              <div class="result-card__label">Inventory Delay Cost</div>
              <div class="result-card__value">${{ formatNumber(results.inventoryCost) }}</div>
              <div class="result-card__formula">{{ formatNumber(results.totalDelayedPallets) }} pallets × ${{ formatNumber(inventoryCost) }}</div>
            </div>
          </div>
        </div>

        <!-- Intermediate Calculations -->
        <div class="results-breakdown">
          <h4 class="breakdown-title">Intermediate Calculations</h4>
          <div class="breakdown-grid">
            <div class="breakdown-item">
              <span class="breakdown-label">Total Dock Capacity:</span>
              <span class="breakdown-value">{{ formatNumber(results.dockCapacity) }} truck-hours</span>
            </div>
            <div class="breakdown-item">
              <span class="breakdown-label">Truck Demand:</span>
              <span class="breakdown-value">{{ formatNumber(results.truckDemand) }} truck-hours</span>
            </div>
            <div class="breakdown-item">
              <span class="breakdown-label">Delay (Yard Congestion):</span>
              <span class="breakdown-value">{{ formatNumber(results.delayHours) }} truck-hours</span>
            </div>
            <div class="breakdown-item">
              <span class="breakdown-label">Dock Time Beyond Free:</span>
              <span class="breakdown-value">{{ formatNumber(results.dockTimeBeyondFree) }} hrs/truck</span>
            </div>
            <div class="breakdown-item">
              <span class="breakdown-label">Yard Wait Per Truck:</span>
              <span class="breakdown-value">{{ formatNumber(results.yardWaitPerTruck) }} hrs/truck</span>
            </div>
            <div class="breakdown-item">
              <span class="breakdown-label">Total Delay Beyond Free Time:</span>
              <span class="breakdown-value">{{ formatNumber(results.delayBeyondFreeTime) }} hrs/truck</span>
            </div>
            <div class="breakdown-item">
              <span class="breakdown-label">Total Detention Hours:</span>
              <span class="breakdown-value">{{ formatNumber(results.totalDetentionHours) }} hrs</span>
            </div>
            <div class="breakdown-item">
              <span class="breakdown-label">Total Delayed Pallets:</span>
              <span class="breakdown-value">{{ formatNumber(results.totalDelayedPallets) }} pallets</span>
            </div>
            <div class="breakdown-item">
              <span class="breakdown-label">Bottleneck Severity:</span>
              <span :class="['breakdown-badge', `breakdown-badge--${results.severity}`]">
                {{ results.severityLabel }}
              </span>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Email Modal -->
    <email-modal
      :show="showEmailModal"
      @update:show="showEmailModal = $event"
      @submit="handleEmailSubmit"
      @cancel="handleEmailCancel"
      title="Enter Your Email"
      description="Please provide your email address to view the calculation results."
      calculation="dock-bottleneck-calculator"
    />
  </div>
</template>

<script>
import EmailModal from '../../../common/EmailModal.vue';

export default {
  name: 'DockBottleneckCalculator',

  components: {
    EmailModal,
  },

  data() {
    return {
      // Section A: Dock & Yard Inputs
      trucksArriving: null,
      usableDoors: null,
      dockTurnTime: null,
      carrierFreeTime: 2.0, // Default to 2 hours
      
      // Section B: Cost Inputs
      detentionFee: null,
      warehouseLabor: null,
      laborCost: null,
      overtimeIdleTime: null,
      palletsPerTruck: null,
      inventoryCost: null,
      
      results: null,
      showEmailModal: false,
      userEmail: null,
      pendingCalculation: false
    };
  },

  mounted() {
    // Check if user info is already stored
    const storedEmail = sessionStorage.getItem('public_view_email');
    const storedName = sessionStorage.getItem('public_view_name');
    const storedPhone = sessionStorage.getItem('public_view_phone');
    
    if (storedEmail) {
      this.userEmail = storedEmail;
      
      // Update header with stored user info
      if (this.$refs.header) {
        this.$refs.header.userEmail = storedEmail;
        if (storedName) {
          this.$refs.header.userName = storedName;
        }
        if (storedPhone) {
          this.$refs.header.userPhone = storedPhone;
        }
      }
    }
  },

  methods: {
    calculate() {
      // Check if user is authenticated
      const isAuthenticated = window.user && window.user.id;
      
      // If not authenticated and no email, show email modal first
      if (!isAuthenticated && !this.userEmail) {
        // Show email modal first
        this.pendingCalculation = true;
        this.showEmailModal = true;
        return;
      }

      // Perform calculation
      this.performCalculation();
    },

    performCalculation() {
      // A. Dock Capacity vs Demand
      const dockCapacity = (this.usableDoors || 0) * (this.dockTurnTime || 0);
      const truckDemand = (this.trucksArriving || 0) * (this.dockTurnTime || 0);
      const delayHours = Math.max(0, truckDemand - dockCapacity);
      
      // B. Detention Time Calculation
      // Step 1: Calculate dock time beyond free time
      const dockTimeBeyondFree = Math.max(0, (this.dockTurnTime || 0) - (this.carrierFreeTime || 0));
      
      // Step 2: Calculate yard wait per truck due to congestion
      const yardWaitPerTruck = Math.max(0, (truckDemand - dockCapacity) / (this.trucksArriving || 1));
      
      // Step 3: Total delay beyond free time per truck
      const delayBeyondFreeTime = dockTimeBeyondFree + yardWaitPerTruck;
      
      // Step 4: Total detention hours for all trucks
      const totalDetentionHours = (this.trucksArriving || 0) * delayBeyondFreeTime;
      
      // C. Cost Calculations
      // A. Detention Cost
      const detentionCost = totalDetentionHours * (this.detentionFee || 0);
      
      // B. Labor Cost (Idle / Overtime)
      const laborCost = (this.warehouseLabor || 0) * (this.overtimeIdleTime || 0) * (this.laborCost || 0);
      
      // C. Inventory Delay Cost
      const totalDelayedPallets = (this.trucksArriving || 0) * (this.palletsPerTruck || 0);
      const inventoryCost = totalDelayedPallets * (this.inventoryCost || 0);
      
      // Total Cost
      const totalCost = detentionCost + laborCost + inventoryCost;
      
      // Determine severity based on delay ratio
      const delayRatio = delayHours / (dockCapacity || 1);
      let severity = 'low';
      let severityLabel = 'Low';
      if (delayRatio > 3) {
        severity = 'critical';
        severityLabel = 'Critical';
      } else if (delayRatio > 2) {
        severity = 'high';
        severityLabel = 'High';
      } else if (delayRatio > 1) {
        severity = 'medium';
        severityLabel = 'Medium';
      }
      
      this.results = {
        totalCost,
        detentionCost,
        laborCost,
        inventoryCost,
        dockCapacity,
        truckDemand,
        delayHours,
        dockTimeBeyondFree,
        yardWaitPerTruck,
        delayBeyondFreeTime,
        totalDetentionHours,
        totalDelayedPallets,
        severity,
        severityLabel
      };
    },

    reset() {
      this.trucksArriving = null;
      this.usableDoors = null;
      this.dockTurnTime = null;
      this.carrierFreeTime = 2.0;
      this.detentionFee = null;
      this.warehouseLabor = null;
      this.laborCost = null;
      this.overtimeIdleTime = null;
      this.palletsPerTruck = null;
      this.inventoryCost = null;
      this.results = null;
    },

    handleEmailSubmit(userInfo) {
      this.userEmail = userInfo.email;
      this.showEmailModal = false;
      
      // Update the header component with the user info
      if (this.$refs.header) {
        this.$refs.header.userEmail = userInfo.email;
        this.$refs.header.userName = userInfo.fullName;
        this.$refs.header.userPhone = userInfo.phone;
      }
      
      // If calculation was pending, perform it now
      if (this.pendingCalculation) {
        this.pendingCalculation = false;
        this.performCalculation();
      }
    },

    handleEmailCancel() {
      this.pendingCalculation = false;
      this.showEmailModal = false;
    },

    formatNumber(value) {
      return new Intl.NumberFormat('en-US', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
      }).format(value);
    }
  }
};
</script>
